import { OperationalZones } from '../types/calculator';

// Interface for zone information needed to generate KML
interface KMLZone {
  name: string;
  center: { lat: number; lng: number };
  radius: number;
  color: string;
}

/**
 * Convert a color string like 'rgba(52, 152, 219, 0.4)' to KML color format 'aabbggrr'
 * KML uses ABGR format (alpha, blue, green, red) with hex values
 */
const convertColorToKML = (rgbaColor: string): string => {
  try {
    // Extract RGBA values from string like 'rgba(52, 152, 219, 0.4)'
    const matches = rgbaColor.match(/rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*(?:,\s*([\d.]+))?\s*\)/);
    
    if (!matches) return 'ff0000ff'; // Default to blue if parsing fails

    const r = parseInt(matches[1], 10).toString(16).padStart(2, '0');
    const g = parseInt(matches[2], 10).toString(16).padStart(2, '0');
    const b = parseInt(matches[3], 10).toString(16).padStart(2, '0');
    const a = matches[4] ? Math.floor(parseFloat(matches[4]) * 255).toString(16).padStart(2, '0') : 'ff';
    
    // KML uses ABGR format (opposite of HTML's RGBA)
    return a + b + g + r;
  } catch (error) {
    console.error('Error converting color:', error);
    return 'ff0000ff'; // Default to blue if parsing fails
  }
};

/**
 * Generate KML document string from zone data
 */
export const generateKML = (
  zones: OperationalZones,
  center: { lat: number; lng: number },
  droneOperatorName: string = 'Drone Operator'
): string => {
  // Calculate cumulative sizes for each zone
  const flightAreaRadius = Number(zones.flightArea) || 0;
  const softGeoFenceRadius = flightAreaRadius + (Number(zones.softGeoFence) || 0);
  const hardGeoFenceRadius = softGeoFenceRadius + (Number(zones.hardGeoFence) || 0);
  const restrictedAreaRadius = hardGeoFenceRadius + (Number(zones.restrictedArea) || 0);
  
  const kmlZones: KMLZone[] = [
    {
      name: 'Flight Area',
      center,
      radius: flightAreaRadius,
      color: 'rgba(52, 152, 219, 0.4)'
    },
    {
      name: 'Soft Geo-fence',
      center,
      radius: softGeoFenceRadius,
      color: 'rgba(241, 196, 15, 0.3)'
    },
    {
      name: 'Hard Geo-fence',
      center,
      radius: hardGeoFenceRadius,
      color: 'rgba(230, 126, 34, 0.25)'
    },
    {
      name: 'Restricted Area',
      center,
      radius: restrictedAreaRadius,
      color: 'rgba(231, 76, 60, 0.25)'
    }
  ];

  // Generate the KML document
  let kml = `<?xml version="1.0" encoding="UTF-8"?>
<kml xmlns="http://www.opengis.net/kml/2.2" xmlns:gx="http://www.google.com/kml/ext/2.2">
  <Document>
    <name>Drone Operational Zones</name>
    <description>Generated by Drone Restricted Area Calculator</description>`;

  // Add each circle as a Placemark with a CirclePolygon
  kmlZones.forEach((zone, index) => {
    const kmlColor = convertColorToKML(zone.color);
    const radiusMeters = zones.unitSystem === 'imperial' ? zone.radius * 0.3048 : zone.radius;
    
    kml += `
    <Placemark>
      <name>${zone.name}</name>
      <description>Cumulative Radius: ${zone.radius.toFixed(1)} ${zones.unitSystem === 'imperial' ? 'ft' : 'm'}</description>
      <Style>
        <LineStyle>
          <color>${kmlColor}</color>
          <width>1.5</width>
        </LineStyle>
        <PolyStyle>
          <color>${kmlColor}</color>
          <fill>1</fill>
          <outline>1</outline>
        </PolyStyle>
      </Style>
      <MultiGeometry>
        <Point>
          <coordinates>${zone.center.lng},${zone.center.lat}</coordinates>
        </Point>
        ${generateCircle(zone.center.lat, zone.center.lng, radiusMeters)}
      </MultiGeometry>
    </Placemark>`;
  });

  // Close the KML document
  kml += `
  </Document>
</kml>`;

  return kml;
};

/**
 * Helper function to generate a KML circle polygon with the given parameters
 */
const generateCircle = (centerLat: number, centerLng: number, radiusMeters: number): string => {
  const points = 64; // Number of points to define the circle
  const coordinates: string[] = [];
  
  for (let i = 0; i < points; i++) {
    const angle = (i * 360) / points;
    const dx = radiusMeters * Math.cos(angle * Math.PI / 180);
    const dy = radiusMeters * Math.sin(angle * Math.PI / 180);
    
    // Calculate the coordinates for this point on the circle
    const point = computeOffset(centerLat, centerLng, dx, dy);
    coordinates.push(`${point.lng},${point.lat},0`);
  }
  
  // Close the polygon by repeating the first coordinate
  coordinates.push(coordinates[0]);
  
  return `<Polygon>
    <outerBoundaryIs>
      <LinearRing>
        <coordinates>${coordinates.join(' ')}</coordinates>
      </LinearRing>
    </outerBoundaryIs>
  </Polygon>`;
};

/**
 * Approximate earth radius in meters
 */
const EARTH_RADIUS = 6378137;

/**
 * Calculate new coordinates given a starting point and offsets in meters
 * This is a simplified version that works for relatively short distances
 */
const computeOffset = (
  lat: number, 
  lng: number, 
  dx: number, 
  dy: number
): { lat: number; lng: number } => {
  // Convert lat to radians
  const latRad = lat * Math.PI / 180;
  
  // Earth circumference at this latitude
  const latCircumference = Math.cos(latRad) * 2 * Math.PI * EARTH_RADIUS;
  
  // Calculate new coordinates
  const newLat = lat + (dy / EARTH_RADIUS) * (180 / Math.PI);
  const newLng = lng + (dx / latCircumference) * 360;
  
  return {
    lat: newLat,
    lng: newLng
  };
};

/**
 * Helper function to save KML content as a file
 */
export const saveKMLFile = (kmlContent: string, filename: string = 'drone_zones.kml'): void => {
  const blob = new Blob([kmlContent], { type: 'application/vnd.google-earth.kml+xml' });
  const url = URL.createObjectURL(blob);
  
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  
  // Clean up
  setTimeout(() => URL.revokeObjectURL(url), 100);
};
